import React, { createContext, useContext, useState, useEffect } from 'react';
import authAPI from '../api/auth';
import {
  authConfigurationError,
  authMode,
  canUseLocalAuth,
  canUsePasswordAuth
} from '../config/auth';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const saveAuthenticatedUser = (userData) => {
    setCurrentUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const clearAuthentication = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setCurrentUser(null);
  };

  const hydrateCurrentUser = async () => {
    const response = await authAPI.getCurrentApplicantUser();
    saveAuthenticatedUser(response.data);
    return response.data;
  };

  // Restore a stored session. Never call local_auth automatically.
  useEffect(() => {
    const token = localStorage.getItem('auth_token');

    if (token) {
      // Validate the token against the currently configured API. This prevents
      // a session created for a local API from leaking into production testing.
      hydrateCurrentUser()
        .catch((error) => {
          console.error('Failed to restore authenticated user:', error);
          clearAuthentication();
        })
        .finally(() => setLoading(false));
      return;
    }

    localStorage.removeItem('user');
    setLoading(false);
    // Authentication is initialized once from browser storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Register a new user
  const register = async (userData) => {
    const response = await authAPI.register(userData);

    if (response.data.auth_token) {
      localStorage.setItem('auth_token', response.data.auth_token);
    }
    
    return response;
  };
  
  // Login user
  const login = async (credentials) => {
    const response = await authAPI.login(credentials);

    const token = response.data.auth_token || response.data.token;
    if (!token) {
      throw new Error('The login response did not include an authentication token');
    }

    localStorage.setItem('auth_token', token);
    
    return response;
  };

  // Test login against a remote API using its regular password endpoint.
  const completePasswordAuth = async (credentials) => {
    if (!canUsePasswordAuth || authConfigurationError) {
      throw new Error(authConfigurationError || 'Password test authentication is disabled');
    }

    await login(credentials);

    try {
      return await hydrateCurrentUser();
    } catch (error) {
      clearAuthentication();
      throw error;
    }
  };

  // Complete local auth without WhatsApp verification.
  const completeLocalAuth = async () => {
    if (!canUseLocalAuth || authConfigurationError) {
      throw new Error(authConfigurationError || 'Local authentication is disabled');
    }

    const response = await authAPI.localAuth();
    const userData = response.data.user;

    localStorage.setItem('auth_token', response.data.auth_token);

    saveAuthenticatedUser(userData);

    return userData;
  };
  
  // Request WhatsApp verification code
  const requestWhatsAppCode = async (phoneNumber = null) => {
    return await authAPI.requestWhatsAppCode(phoneNumber);
  };
  
  // Verify WhatsApp code
  const verifyWhatsAppCode = async (code, phoneNumber = null) => {
    const response = await authAPI.verifyWhatsAppCode(code, phoneNumber);
    
    // Store user data
    const userData = response.data.user;
    setCurrentUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Update token if provided
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    
    return response;
  };
  
  // Request password reset
  const requestPasswordReset = async (email) => {
    return await authAPI.requestPasswordReset(email);
  };
  
  // Verify password reset code
  const verifyPasswordResetCode = async (email, code) => {
    const response = await authAPI.verifyPasswordResetCode(email, code);
    
    // Store token for password reset
    if (response.data.token) {
      localStorage.setItem('reset_token', response.data.token);
    }
    
    return response;
  };
  
  // Reset password
  const resetPassword = async (newPassword, token = null) => {
    const resetToken = token || localStorage.getItem('reset_token');
    
    if (!resetToken) {
      throw new Error('No reset token available');
    }
    
    const response = await authAPI.resetPassword(newPassword, resetToken);
    
    // Clean up reset token
    localStorage.removeItem('reset_token');
    
    // Store new auth token if provided
    if (response.data.auth_token) {
      localStorage.setItem('auth_token', response.data.auth_token);
    }
    
    return response;
  };
  
  // Logout user
  const logout = () => {
    clearAuthentication();
  };
  
  const value = {
    currentUser,
    loading,
    authMode,
    authConfigurationError,
    canUseLocalAuth,
    canUsePasswordAuth,
    completeLocalAuth,
    completePasswordAuth,
    registerUser: register,
    login,
    logout,
    requestWhatsAppCode,
    verifyWhatsAppCode,
    requestPasswordReset,
    verifyPasswordResetCode,
    resetPassword
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
